// Rendered in place of an empty cell.
export const DASH = '—';

// Feature explainer shown in the "?" tooltip next to a row label: a bold heading + a
// plain-language description (rendered by FeatureTooltip → HoverCard.Title / .Description).
export type ComparisonRowTooltip = {
  title: string;
  description: string;
};

export type ComparisonRow = {
  label: string;
  // The minutes row is derived from live API data at render time; `values` acts as a fallback.
  isMinutesRow?: boolean;
  key: string;
  // Explanatory copy shown in the "?" tooltip next to the label (icon-only trigger).
  tooltip?: ComparisonRowTooltip;
  // [Creator, Creator Pro, Business, Enterprise]
  values: [string, string, string, string];
};

// "Compare what changes when you scale" — rows/values mirror the Figma pricing design.
// Rows beyond COLLAPSED_ROW_COUNT are revealed by the "See all features" toggle.
export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    label: 'Included localization minutes / year',
    isMinutesRow: true,
    key: 'minutes',
    tooltip: {
      title: 'Included localization minutes / month',
      description: 'The number of minutes you can use each month for translation or dubbing.',
    },
    values: [DASH, DASH, DASH, 'Custom'],
  },
  {
    // Visible only when the billing toggle is set to Annual (see EXTRA_MINUTES_ROW_KEY).
    label: 'Extra minutes',
    key: 'extraMinutes',
    tooltip: {
      title: 'Extra minutes',
      description: 'You can buy additional minutes anytime on annual plans.',
    },
    values: ['Upgrade to Pro', '$3 / extra minute', '$3 / extra minute', 'Custom'],
  },
  {
    label: 'Saved custom voices',
    key: 'savedVoices',
    tooltip: {
      title: 'Saved custom voices',
      description: 'Save cloned voices so you can reuse them across future projects.',
    },
    values: [DASH, '3', '10 shared team voices', 'Custom voice library'],
  },
  {
    label: 'Multi-speaker lip-sync',
    key: 'lipSync',
    tooltip: {
      title: 'Multi-speaker lip-sync',
      description: 'Matches mouth movements to translated audio for more natural-looking dubbing.',
    },
    values: [DASH, 'Included', 'Included', 'Included'],
  },
  {
    label: 'Script, subtitle and voice controls',
    key: 'controls',
    tooltip: {
      title: 'Script, subtitle and voice controls',
      description: 'Edit the transcript, timestamps, speaker labels, and voice settings directly.',
    },
    values: [DASH, 'Included', 'Included', 'Included'],
  },
  {
    label: 'Glossary and terminology',
    key: 'glossary',
    tooltip: {
      title: 'Glossary and terminology',
      description: 'Save custom terms so the same words are translated consistently every time.',
    },
    values: [DASH, 'Personal glossary', 'Shared brand glossary', 'Managed terminology workflow'],
  },
  {
    label: 'Multi-language workflow',
    key: 'multiLanguage',
    tooltip: {
      title: 'Multi-language workflow',
      description: 'Translate a single video into several languages in one project.',
    },
    values: [DASH, DASH, 'Multi-video × multi-language batch', 'Custom batch pipelines'],
  },
  {
    label: 'Review workflow',
    key: 'reviewWorkflow',
    tooltip: {
      title: 'Review workflow',
      description: "Send translated content to reviewers for approval before it's published.",
    },
    values: [DASH, DASH, 'Reviewer roles and approval', 'Managed QA / SME approval'],
  },
  {
    label: 'Team workspace',
    key: 'teamWorkspace',
    tooltip: {
      title: 'Team workspace',
      description: 'Invite teammates to collaborate on projects in one shared space.',
    },
    values: [DASH, 'Up to 3 members', 'Up to 5 members + guest reviewers', 'Custom roles'],
  },
  // --- revealed by "See all features" ---
  {
    label: 'Processing speed',
    key: 'processingSpeed',
    tooltip: {
      title: 'Processing speed',
      description: 'How quickly your videos are processed and rendered.',
    },
    values: ['Standard queue', 'Standard queue', 'Priority queue', 'Dedicated / priority'],
  },
  {
    label: 'API and integrations',
    key: 'api',
    tooltip: {
      title: 'API and integrations',
      description: 'Connect Rask to your existing tools and automate your workflow.',
    },
    values: [DASH, 'Limited / sandbox API', 'Full API access', 'Custom integrations'],
  },
  {
    label: 'Security and procurement',
    key: 'security',
    tooltip: {
      title: 'Security and procurement',
      description: 'Support for contracts, invoicing, and compliance documentation.',
    },
    values: [DASH, DASH, 'Standard', 'SSO, DPA, compliance review'],
  },
  {
    label: 'SRT import/export',
    key: 'srt',
    tooltip: {
      title: 'SRT import / export',
      description: 'Download subtitle files or upload your own to sync with dubbing.',
    },
    values: ['Export only', 'Included', 'Included', 'Included'],
  },
  {
    label: 'Captions & subtitle styling',
    key: 'captions',
    tooltip: {
      title: 'Captions & subtitle styling',
      description: 'Add on-screen captions and adjust how they look.',
    },
    values: ['Basic', 'Advanced presets', 'Advanced presets', 'Custom'],
  },
];

// The "Extra minutes" row only applies to annual plans → hidden when the toggle is Monthly.
export const EXTRA_MINUTES_ROW_KEY = 'extraMinutes';

// Rows visible before the user expands "See all features".
export const COLLAPSED_ROW_COUNT = 9;
